import React, { Fragment, useCallback } from 'react';
import clsx from 'clsx';
import { Nullable } from 'global';
import { formatCurrency } from 'utils';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { LocalShipping, AddCircleOutline } from '@mui/icons-material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { Box, Button, Stack, Typography, Divider, CircularProgress } from '@mui/material';
import { CurrencyInputField, InterestInputField, DateField, InputField } from 'components/form';
import { calculateSalesCommissionFlat, calculateSalesCommissionPercent } from 'utils/equipmentCommissionCalculations';

export interface EquipmentListItem {
  id: string;
  name: string;
  markupFlat: number;
  salePrice: number;
  markupPercent: number;
  wholesalePrice: number;
  dateSold: Nullable<string>;
  salesCommissionFlat: number;
  salesCommissionPercent: number;
  saleRep: Nullable<{ id: string; fullName: string }>;
}

export interface EquipmentTotals {
  markupFlat: number;
  salePrice: number;
  wholesalePrice: number;
  salesCommissionFlat: number;
}

export interface EquipmentRevenueCardProps {
  totals: EquipmentTotals;
  isEquipmentListLoading: boolean;
  isEquipmentListUpdating: boolean;
  equipmentList: EquipmentListItem[];
  handleRedirectToGeneralTab: VoidFunction;
  handleUpdateEquipmentList: (dto: EquipmentListItem[]) => Promise<void>;
  getInitialEquipmentList: () => Promise<{ equipments: EquipmentListItem[] }>;
  onFormStateChange?: (
    isDirty: boolean,
    handleSave: () => Promise<void>,
    handleReset: VoidFunction,
    formValues: EquipmentListItem[]
  ) => void;
}

const totalsRenderConfig: Array<{ key: keyof EquipmentTotals; label: string }> = [
  { key: 'salePrice', label: 'Sale Price' },
  { key: 'wholesalePrice', label: 'Wholesale Price' },
  { key: 'markupFlat', label: 'Markup' },
  { key: 'salesCommissionFlat', label: 'Sales Rep Commissions' },
];

export const RevenueCard = ({
  totals,
  equipmentList,
  isEquipmentListLoading,
  isEquipmentListUpdating,
  getInitialEquipmentList,
  handleUpdateEquipmentList,
  handleRedirectToGeneralTab,
  onFormStateChange,
}: EquipmentRevenueCardProps) => {
  const {
    control,
    reset,
    getValues,
    setValue,
    formState: { isDirty },
  } = useForm({ defaultValues: getInitialEquipmentList });

  const { fields } = useFieldArray({ control, name: 'equipments' });
  const equipmentsCurrentValues = useWatch({ control, name: 'equipments' });

  const handleUpdateEquipments = useCallback(async () => {
    const currentFormValue = getValues('equipments');

    await handleUpdateEquipmentList(currentFormValue);
  }, [getValues, handleUpdateEquipmentList]);

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  const handleCalcCommissionsPercent = (equipmentIndex: number) => {
    const { salesCommissionFlat, markupFlat } = getValues(`equipments.${equipmentIndex}`);
    const salesCommissionPercent = calculateSalesCommissionPercent(markupFlat, salesCommissionFlat);

    setValue(`equipments.${equipmentIndex}.salesCommissionPercent`, salesCommissionPercent, { shouldDirty: true });
  };

  const handleCalcCommissionsFlat = (equipmentIndex: number) => {
    const { markupFlat, salesCommissionPercent } = getValues(`equipments.${equipmentIndex}`);
    const salesCommissionFlat = calculateSalesCommissionFlat(markupFlat, salesCommissionPercent);

    setValue(`equipments.${equipmentIndex}.salesCommissionFlat`, salesCommissionFlat, { shouldDirty: true });
  };

  const handleCalcMarkup = (equipmentIndex: number) => {
    const { salePrice, wholesalePrice } = getValues(`equipments.${equipmentIndex}`);

    const newMarkupFlat = salePrice - wholesalePrice;
    const markupPercent = (newMarkupFlat / salePrice) * 100;

    setValue(`equipments.${equipmentIndex}.markupFlat`, +newMarkupFlat.toFixed(2), { shouldDirty: true });
    setValue(`equipments.${equipmentIndex}.markupPercent`, +markupPercent.toFixed(2), { shouldDirty: true });

    handleCalcCommissionsFlat(equipmentIndex);
  };

  const currentTotals = calcEquipmentRevenuesTotals(equipmentsCurrentValues ?? []);

  React.useEffect(() => {
    if (onFormStateChange) {
      const formValues = getValues('equipments');
      onFormStateChange(isDirty, handleUpdateEquipments, handleReset, formValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, onFormStateChange, equipmentsCurrentValues]);

  return (
    <Box className="revenueCard">
      <RenderWithFallback
        isLoading={isEquipmentListLoading}
        fallBack={
          <Stack alignItems="center" justifyContent="center">
            <CircularProgress />
            <Typography>Loading equipment...</Typography>
          </Stack>
        }
      >
        <RenderWithCondition
          condition={!!equipmentList.length}
          fallBack={
            <Stack alignItems="center" justifyContent="center" gap="0.5rem">
              <Typography>There is no equipment for this VO</Typography>
              <Button variant="contained" startIcon={<AddCircleOutline />} onClick={handleRedirectToGeneralTab}>
                Add
              </Button>
            </Stack>
          }
        >
          <Box className="revenueCardHeader">
            <Box className="revenueCardHeaderIconWithTitle">
              <Box className="revenueCardHeaderIcon">
                <LocalShipping />
              </Box>

              <Typography variant="subtitle1" component="span">
                Equipment Sale
              </Typography>
            </Box>
          </Box>

          <Box className="revenueEquipmentContent">
            {fields.map((equipment, index) => (
              <Fragment key={equipment.id}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {equipment.name}
                </Typography>

                <Box key={equipment.id} className="revenueEquipmentList">
                  <Box className="revenueEquipmentInfo">
                    <CurrencyInputField
                      control={control}
                      label="Sale Price"
                      name={`equipments.${index}.salePrice`}
                      textFieldProps={{ variant: 'outlined' }}
                      customOnChange={(nextValue, name) => {
                        setValue(name, nextValue);
                        handleCalcMarkup(index);
                      }}
                    />

                    <CurrencyInputField
                      control={control}
                      label="Wholesale Price"
                      name={`equipments.${index}.wholesalePrice`}
                      textFieldProps={{ variant: 'outlined' }}
                      customOnChange={(nextValue, name) => {
                        setValue(name, nextValue);
                        handleCalcMarkup(index);
                      }}
                    />

                    <DateField
                      disabled
                      label="Date Sold"
                      control={control}
                      name={`equipments.${index}.dateSold`}
                      slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
                    />

                    <Stack direction="row" gap="0.5rem">
                      <CurrencyInputField
                        allowNegative
                        control={control}
                        label="Markup ($)"
                        name={`equipments.${index}.markupFlat`}
                        textFieldProps={{ variant: 'outlined', disabled: true }}
                      />

                      <InterestInputField
                        allowNegative
                        control={control}
                        label="Markup (%)"
                        name={`equipments.${index}.markupPercent`}
                        textFieldProps={{ variant: 'outlined', disabled: true }}
                      />
                    </Stack>

                    <CurrencyInputField
                      allowNegative
                      control={control}
                      label="Sales Rep Commissions ($)"
                      textFieldProps={{ variant: 'outlined' }}
                      name={`equipments.${index}.salesCommissionFlat`}
                      customOnChange={(nextValue, name) => {
                        setValue(name, nextValue);
                        handleCalcCommissionsPercent(index);
                      }}
                    />

                    <InterestInputField
                      allowNegative
                      control={control}
                      label="Sales Rep Commissions (%)"
                      textFieldProps={{ variant: 'outlined' }}
                      name={`equipments.${index}.salesCommissionPercent`}
                      customOnChange={(nextValue, name) => {
                        setValue(name, nextValue);
                        handleCalcCommissionsFlat(index);
                      }}
                    />

                    <InputField
                      control={control}
                      label="Sales Rep"
                      name={`equipments.${index}.saleRep.fullName`}
                      inputProps={{ variant: 'outlined', disabled: true, sx: { gridColumn: '1 / -1' } }}
                    />
                  </Box>
                </Box>

                <Divider />
              </Fragment>
            ))}

            <Box className="revenueEquipmentTotals">
              {totalsRenderConfig.map(({ key, label }) => (
                <Box
                  key={key}
                  className={clsx('revenueEquipmentTotal', isEquipmentListUpdating && 'revenueEquipmentTotal-loading')}
                >
                  <Typography color="textDisabled">Total {label}</Typography>

                  <RenderWithCondition
                    condition={currentTotals[key] === totals[key]}
                    fallBack={<Typography color="warning">Save for calculation</Typography>}
                  >
                    <Typography>{formatCurrency(totals[key], true)}</Typography>
                  </RenderWithCondition>
                </Box>
              ))}
            </Box>
          </Box>
        </RenderWithCondition>
      </RenderWithFallback>
    </Box>
  );
};

function calcEquipmentRevenuesTotals(equipmentsCurrentValues: EquipmentListItem[]) {
  return equipmentsCurrentValues.reduce<EquipmentTotals>(
    (acc, equipment) => {
      acc.markupFlat += equipment.markupFlat || 0;
      acc.salePrice += equipment.salePrice || 0;
      acc.wholesalePrice += equipment.wholesalePrice || 0;
      acc.salesCommissionFlat += +equipment.salesCommissionFlat?.toFixed(2) || 0;

      return acc;
    },
    { salePrice: 0, wholesalePrice: 0, markupFlat: 0, salesCommissionFlat: 0 }
  );
}
